import i18n from '@/utils/i18n';
import { isValidNumber } from '@/utils/number';
import { PeerRange } from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import {
  PillFilterSection,
  PricingBookmarkedFilterValue,
  PricingEditsFilterValue,
  PricingFilterSectionKind,
  PricingFilterSpecialSectionId,
  PricingPeerAverageFilterValue,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { roundPercentageToNearestTenth } from '@sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';

// Shared across every screen's section builder — only the row-id accessor
// differs (variant pack UUID for item-scoped screens, restaurant UUID for
// customer-scoped ones).
export const buildEditsSection = <T>(
  editCounts: Record<string, number>,
  getRowId: (row: T) => string,
): PillFilterSection<T> => ({
  getValues: (row) => [
    (editCounts[getRowId(row)] ?? 0) > 0
      ? PricingEditsFilterValue.Has
      : PricingEditsFilterValue.None,
  ],
  id: PricingFilterSpecialSectionId.Edits,
  kind: PricingFilterSectionKind.Pill,
  options: [
    {
      label: i18n.t('pricing.filters.edits.has'),
      value: PricingEditsFilterValue.Has,
    },
    {
      label: i18n.t('pricing.filters.edits.none'),
      value: PricingEditsFilterValue.None,
    },
  ],
  singleSelect: true,
  title: i18n.t('pricing.filters.edits.title'),
});

export const buildBookmarkedSection = <T>(
  bookmarkedByRow: Record<string, boolean>,
  getRowId: (row: T) => string,
): PillFilterSection<T> => ({
  getValues: (row) => [
    bookmarkedByRow[getRowId(row)]
      ? PricingBookmarkedFilterValue.Bookmarked
      : PricingBookmarkedFilterValue.NotBookmarked,
  ],
  id: PricingFilterSpecialSectionId.Bookmarked,
  kind: PricingFilterSectionKind.Pill,
  options: [
    {
      label: i18n.t('pricing.filters.bookmarked.bookmarked'),
      value: PricingBookmarkedFilterValue.Bookmarked,
    },
    {
      label: i18n.t('pricing.filters.bookmarked.not_bookmarked'),
      value: PricingBookmarkedFilterValue.NotBookmarked,
    },
  ],
  singleSelect: true,
  title: i18n.t('pricing.filters.bookmarked.title'),
});

interface PeerAverageFilterConfig<T> {
  // Decoupled from a concrete row shape (peerRange doesn't exist on every
  // screen's row type) — the caller supplies both accessors.
  getCurrentGrossMargin: (row: T) => number;
  getPeerRange: (row: T) => PeerRange | null;
}

export const buildPeerAverageSection = <T>({
  getCurrentGrossMargin,
  getPeerRange,
}: PeerAverageFilterConfig<T>): PillFilterSection<T> => ({
  getValues: (row) => {
    const peerRange = getPeerRange(row);
    const min = peerRange?.minGrossProfitInPercentage;
    const max = peerRange?.maxGrossProfitInPercentage;
    if (!isValidNumber(min) || !isValidNumber(max)) {
      return [];
    }

    const current = roundPercentageToNearestTenth(getCurrentGrossMargin(row));
    const midpoint = roundPercentageToNearestTenth((min + max) / 2);

    return [
      current > midpoint
        ? PricingPeerAverageFilterValue.Above
        : PricingPeerAverageFilterValue.Below,
    ];
  },
  id: PricingFilterSpecialSectionId.PeerAverage,
  kind: PricingFilterSectionKind.Pill,
  options: [
    {
      label: i18n.t('pricing.filters.peer_average.above'),
      value: PricingPeerAverageFilterValue.Above,
    },
    {
      label: i18n.t('pricing.filters.peer_average.below'),
      value: PricingPeerAverageFilterValue.Below,
    },
  ],
  singleSelect: true,
  title: i18n.t('pricing.filters.peer_average.title'),
});
